import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import type { Machine } from "@/machines/types/machine";
import { useAggregatedMetadata } from "@/machines/hooks/use-aggregated-metadata";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";

const formatTime = (t: TFunction<"map">, seconds: number | undefined): string => {
  if (seconds == null || !Number.isFinite(seconds)) return "-";
  return t("kpis.minutes", { value: formatNumber(Math.round(seconds / 60)) });
};

/**
 * Location-aggregate KPIs for the `/map` panel — the same figures as the
 * location-detail KPI stripe (`useAggregatedMetadata`), laid out as a vertical
 * card list for the right-hand drawer.
 */
const MapKpisPanel = ({ machines }: { machines: Machine[] }) => {
  const { t } = useTranslation("map");
  const { value } = useDateTimeRangeStore();
  const deviceMxIds = useMemo(
    () => machines.map((m) => m.device_mxid).filter(Boolean),
    [machines],
  );

  const { data: agg, isLoading } = useAggregatedMetadata({
    deviceMxIds,
    start: value.fromDate,
    end: value.toDate,
  });

  const items = useMemo(() => {
    const tl = agg?.aggregated.truckLoading;
    const wf = agg?.aggregated.waitingForTruck;
    return [
      { label: t("kpis.avgCycleTime"), value: formatTime(t, tl?.avgCycleTime) },
      { label: t("kpis.targetCycleTime"), value: formatTime(t, tl?.targetCycleTime) },
      {
        label: t("kpis.longCycles"),
        value: tl
          ? t("kpis.countWithPercent", {
              count: formatNumber(tl.longCyclesCnt),
              percent: formatNumber(tl.longCyclesPerc, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              }),
            })
          : "-",
      },
      { label: t("kpis.bucketCount"), value: tl ? formatNumber(tl.bucketCount) : "-" },
      { label: t("kpis.avgWaitTime"), value: formatTime(t, wf?.avgWaitTime) },
      {
        label: t("kpis.longWaits"),
        value: wf
          ? t("kpis.countWithPercent", {
              count: formatNumber(wf.longWaitsCnt),
              percent: formatNumber(wf.longWaitsPerc, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              }),
            })
          : "-",
      },
      { label: t("kpis.boulderCount"), value: tl ? formatNumber(tl.boulderCount) : "-" },
    ];
  }, [agg, t]);

  if (deviceMxIds.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {t("kpis.empty")}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map((item) => (
        <div key={item.label} className="rounded-lg border bg-card p-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {item.label}
          </div>
          <div className="mt-1 text-xl font-bold">{isLoading ? "…" : item.value}</div>
        </div>
      ))}
    </div>
  );
};

export default MapKpisPanel;
